import { useQuery } from "@tanstack/react-query"
import { getFmvReview, getFmvProofFiles, getFmvProgress, getFmvManagers, getDmFmvReview, getFmvStagingList, getClientEmails, getClientEmailAttachments, getProofStatuses, getFmvActivity, getFmvActivityUnseenCount, getFmvChangeReport } from "@/api/companies"
import type { FmvManager, ProofStatusEntry } from "@/api/companies"
import type { FmvReviewEntry, FmvProofFile, DmFmvEntry, FmvStagingListItem, ClientEmail, ClientEmailAttachment, FmvActivityItem } from "@/types/company"

export const useFmvReview = (viewAs?: string) => {
  const query = useQuery<FmvReviewEntry[]>({
    queryKey: ["fmvReview", viewAs],
    queryFn: () => getFmvReview(viewAs),
    staleTime: 1000 * 60 * 5,
  })

  return {
    ...query,
    data: query.data || [],
  }
}

export const useFmvProofFiles = (folderPath: string | null) => {
  return useQuery<FmvProofFile[]>({
    queryKey: ["fmvProofFiles", folderPath],
    queryFn: () => getFmvProofFiles(folderPath!),
    enabled: !!folderPath,
    staleTime: 1000 * 60 * 10,
  })
}

export const useFmvProgress = (viewAs?: string) => {
  return useQuery<{ total: number; updated: number; quarterLabel: string }>({
    queryKey: ["fmvProgress", viewAs],
    queryFn: () => getFmvProgress(viewAs),
    staleTime: 1000 * 60 * 5,
  })
}

export const useFmvManagers = (enabled: boolean) => {
  return useQuery<FmvManager[]>({
    queryKey: ["fmvManagers"],
    queryFn: getFmvManagers,
    enabled,
    staleTime: 1000 * 60 * 30,
  })
}

export const useFmvStagingList = (viewAs?: string) => {
  return useQuery<FmvStagingListItem[]>({
    queryKey: ["fmvStagingList", viewAs],
    queryFn: () => getFmvStagingList(viewAs),
    staleTime: 1000 * 60 * 2,
  })
}

export const useClientEmails = (clientEmail: string | null) => {
  return useQuery<{ emails: ClientEmail[]; microsoftConnected: boolean }>({
    queryKey: ["clientEmails", clientEmail],
    queryFn: () => getClientEmails(clientEmail!),
    enabled: !!clientEmail,
    staleTime: 1000 * 60 * 5,
  })
}

export const useClientEmailAttachments = (messageId: string | null) => {
  return useQuery<ClientEmailAttachment[]>({
    queryKey: ["clientEmailAttachments", messageId],
    queryFn: () => getClientEmailAttachments(messageId!),
    enabled: !!messageId,
    staleTime: 1000 * 60 * 10,
  })
}

export const useProofStatuses = (viewAs?: string) => {
  return useQuery<ProofStatusEntry[]>({
    queryKey: ["proofStatuses", viewAs],
    queryFn: () => getProofStatuses(viewAs),
    staleTime: 1000 * 60 * 5,
  })
}

export const useFmvActivity = (viewAs?: string) => {
  return useQuery<{ items: FmvActivityItem[]; lastSeenAt: string | null }>({
    queryKey: ["fmvActivity", viewAs],
    queryFn: () => getFmvActivity(viewAs),
    staleTime: 1000 * 60 * 2,
  })
}

export const useFmvActivityUnseenCount = (viewAs?: string) => {
  return useQuery<number>({
    queryKey: ["fmvActivityUnseenCount", viewAs],
    queryFn: () => getFmvActivityUnseenCount(viewAs),
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 60,
  })
}

export const useDmFmvReview = (viewAs?: string, includeTest?: boolean) => {
  const query = useQuery<DmFmvEntry[]>({
    queryKey: ["dmFmvReview", viewAs, includeTest],
    queryFn: () => getDmFmvReview(viewAs, includeTest),
    staleTime: 1000 * 60 * 5,
  })

  return {
    ...query,
    data: query.data || [],
  }
}

export const useFmvChangeReport = (startDate: string, endDate: string) => {
  return useQuery({
    queryKey: ["fmvChangeReport", startDate, endDate],
    queryFn: () => getFmvChangeReport(startDate, endDate),
    enabled: !!startDate && !!endDate,
    staleTime: 1000 * 60 * 5,
  })
}
